import React, { useState, useEffect, useMemo } from 'react';
import { UserProfile } from '../types';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Shield,
  Activity,
  CheckCircle2,
  Ban,
  Radio,
  AlertTriangle,
  Sparkles,
  Search,
  Crown,
  Bell,
  Download,
  RefreshCw,
  Lock,
  Unlock,
  KeyRound,
  Trash2,
  Check,
  Send,
  HelpCircle,
  FileText,
  Server,
  Cpu,
  Clock,
  ExternalLink,
  ChevronRight,
  UserPlus,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';

interface AdminViewProps {
  currentUser: UserProfile;
  onUpdateProfile?: (profile: Partial<UserProfile>) => void;
}

type AdminTab = 'overview' | 'users' | 'broadcast' | 'conversations' | 'reports' | 'system';

export const AdminView: React.FC<AdminViewProps> = ({ currentUser, onUpdateProfile }) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);

  // Elevation state (for non-admins)
  const [passkeyInput, setPasskeyInput] = useState('');
  const [elevating, setElevating] = useState(false);
  const [elevationError, setElevationError] = useState<string | null>(null);

  // Users Tab state
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState<'all' | 'admins' | 'verified' | 'premium' | 'banned'>('all');
  const [actionLoadingUser, setActionLoadingUser] = useState<string | null>(null);

  // Broadcast Tab state
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastSeverity, setBroadcastSeverity] = useState<'info' | 'warning' | 'urgent'>('info');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [broadcastSuccess, setBroadcastSuccess] = useState(false);

  // Fetch admin stats
  const fetchStats = async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    setErrorMessage(null);
    try {
      const res = await fetch('/api/admin/stats');
      if (!res.ok) {
        throw new Error('فشل استرجاع بيانات الإدارة من الخادم');
      }
      const data = await res.json();
      setStats(data);
    } catch (e: any) {
      console.error('Error loading admin stats:', e);
      setErrorMessage(e.message || 'تعذر الاتصال بمركز إدارة وتين');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Handle Elevating to Admin
  const handleElevate = async (customPass?: string) => {
    const key = customPass !== undefined ? customPass : passkeyInput;
    setElevating(true);
    setElevationError(null);
    try {
      const res = await fetch('/api/admin/elevate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: currentUser.username,
          passkey: key,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'رمز المرور غير صحيح');
      }
      soundEffects.playSent();
      onUpdateProfile?.({ isAdmin: true });
      fetchStats();
    } catch (err: any) {
      setElevationError(err.message || 'فشل تفعيل صلاحية الإدارة');
    } finally {
      setElevating(false);
    }
  };

  // Perform User Moderation Action
  const handleUserAction = async (
    targetUsername: string,
    action: 'ban' | 'unban' | 'toggle_verify' | 'toggle_admin' | 'toggle_premium' | 'clear_lockout' | 'reset_password' | 'delete_user'
  ) => {
    setActionLoadingUser(targetUsername);
    try {
      const res = await fetch('/api/admin/user-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUsername, action }),
      });
      const data = await res.json();
      if (res.ok) {
        soundEffects.playTap();
        const actionLabels: Record<string, string> = {
          ban: 'تم حظر المستخدم بنجاح',
          unban: 'تم إلغاء حظر المستخدم',
          toggle_verify: 'تم تعديل شارة التوثيق',
          toggle_admin: 'تم تعديل رتبة المشرف',
          toggle_premium: 'تم تعديل اشتراك وتين بريميوم',
          clear_lockout: 'تم فك قفل تسجيل الدخول',
          reset_password: 'تم إعادة تعيين كلمة المرور إلى wateen123',
          delete_user: 'تم حذف الحساب نهائياً',
        };
        setActionFeedback(actionLabels[action] || 'تم تنفيذ الإجراء بنجاح');
        setTimeout(() => setActionFeedback(null), 3500);
        await fetchStats();
      } else {
        alert(data.error || 'فشل تنفيذ الإجراء');
      }
    } catch (e: any) {
      console.error(e);
      alert('حدث خطأ أثناء تنفيذ الإجراء');
    } finally {
      setActionLoadingUser(null);
    }
  };

  // Handle Broadcast Submission
  const handleSendBroadcast = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!broadcastText.trim()) return;

    setSendingBroadcast(true);
    try {
      const res = await fetch('/api/admin/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: broadcastTitle,
          text: broadcastText,
          severity: broadcastSeverity,
        }),
      });
      if (res.ok) {
        soundEffects.playSent();
        setBroadcastSuccess(true);
        setBroadcastTitle('');
        setBroadcastText('');
        setTimeout(() => setBroadcastSuccess(false), 4000);
      } else {
        alert('فشل إرسال البث');
      }
    } catch (e) {
      console.error(e);
      alert('تعذر إرسال البث العام');
    } finally {
      setSendingBroadcast(false);
    }
  };

  // Handle Report Resolution
  const handleResolveReport = async (reportId: string, status: 'resolved' | 'dismissed') => {
    try {
      await fetch('/api/admin/resolve-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId, status }),
      });
      soundEffects.playTap();
      fetchStats();
    } catch (e) {
      console.error(e);
    }
  };

  // Export JSON Database Backup
  const handleExportBackup = () => {
    soundEffects.playTap();
    if (!stats) return;
    const exportData = {
      exportedAt: new Date().toISOString(),
      platform: 'Wateen Messenger Platform',
      version: '2.0.0',
      totalUsers: stats.totalUsers,
      totalConversations: stats.totalConversations,
      totalMessages: stats.totalMessages,
      users: stats.users,
      conversations: stats.conversations,
      reports: stats.reports,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wateen_backup_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Filtered Users List
  const filteredUsers = useMemo(() => {
    if (!stats?.users) return [];
    return stats.users.filter((u: any) => {
      // Search
      const term = userSearch.toLowerCase().trim();
      const matchSearch =
        !term ||
        u.username?.toLowerCase().includes(term) ||
        u.displayName?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.country?.toLowerCase().includes(term);

      if (!matchSearch) return false;

      // Filter
      if (userFilter === 'admins') return !!u.isAdmin;
      if (userFilter === 'verified') return !!u.isVerified;
      if (userFilter === 'premium') return !!u.isPremium;
      if (userFilter === 'banned') return !!u.isBanned;
      return true;
    });
  }, [stats?.users, userSearch, userFilter]);

  // If user is not yet an admin, show the elegant security passkey elevation screen
  if (!currentUser.isAdmin) {
    const isOwnerCandidate =
      currentUser.username === 'aymen' ||
      (currentUser.email && currentUser.email.toLowerCase().includes('aymenansta7'));

    return (
      <div
        dir="rtl"
        className="flex-1 overflow-y-auto p-4 sm:p-8 bg-[#060911] font-['Cairo'] flex items-center justify-center select-none"
      >
        <div className="max-w-md w-full bg-[#0d1322] border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden text-center">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-tr from-emerald-500/20 to-teal-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-xl shadow-emerald-500/10">
            <Shield className="w-8 h-8" />
          </div>

          <h2 className="text-xl font-black text-white mb-1.5">بوابة إدارة منصة وتين (Admin HQ)</h2>
          <p className="text-xs text-slate-400 mb-6 leading-relaxed">
            هذا القسم محمي ومخصص للمشرفين وإدارة المنصة لمتابعة الإحصائيات، إدارة الحسابات، وضبط معايير الأمان والتفاعل.
          </p>

          {isOwnerCandidate && (
            <div className="mb-5 p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-right">
              <div className="flex items-center gap-2 text-amber-300 font-bold text-xs mb-1">
                <Crown className="w-4 h-4 text-amber-400" />
                <span>مرحباً أيمن (المالك والمؤسس) 👑</span>
              </div>
              <p className="text-[11px] text-slate-300 mb-3 leading-relaxed">
                تم التعرف على حسابك كمالك لمنصة وتين. يمكنك تفعيل صلاحيات الإدارة الفورية لحسابك مباشرة بضغطة زر واحدة.
              </p>
              <button
                onClick={() => handleElevate('admin123')}
                disabled={elevating}
                className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-400 hover:to-yellow-500 text-slate-950 font-black text-xs flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 transition active:scale-95 disabled:opacity-50"
              >
                {elevating ? (
                  <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                <span>تفعيل صلاحيات الإدارة فوراً كمالك</span>
              </button>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleElevate();
            }}
            className="space-y-3 text-right"
          >
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1.5">
                مفتاح الإشراف السري (Admin Passkey)
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={passkeyInput}
                  onChange={(e) => setPasskeyInput(e.target.value)}
                  placeholder="أدخل رمز المرور الإداري..."
                  className="w-full py-2.5 px-3.5 pl-10 rounded-xl bg-[#141b2a] border border-slate-800 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 transition font-mono"
                />
                <KeyRound className="w-4 h-4 text-slate-500 absolute left-3 top-3 pointer-events-none" />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                المفتاح الافتراضي المعتمد للمشرفين: <code className="text-emerald-400 font-mono">admin123</code> أو <code className="text-emerald-400 font-mono">wateen2026</code>
              </div>
            </div>

            {elevationError && (
              <div className="p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>{elevationError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={elevating || !passkeyInput.trim()}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 transition active:scale-95 disabled:opacity-40"
            >
              {elevating ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Unlock className="w-4 h-4" />
              )}
              <span>التحقق وتأكيد الدخول للإدارة</span>
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Loading State
  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-[#060911] font-['Cairo'] text-slate-400 text-xs">
        <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-3 animate-pulse">
          <Shield className="w-6 h-6" />
        </div>
        <div className="flex items-center gap-2 font-medium">
          <span className="animate-spin w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full" />
          <span>جاري الاتصال بمركز إدارة وتين اللحظي...</span>
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="flex-1 overflow-y-auto p-3 sm:p-6 bg-[#060911] font-['Cairo'] text-slate-100 select-none">
      <div className="max-w-6xl mx-auto space-y-5">
        {/* Top Header */}
        <div className="p-4 sm:p-5 rounded-3xl bg-gradient-to-l from-[#0e1626] to-[#0a0f1d] border border-slate-800 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-lg shadow-emerald-500/25 shrink-0">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg sm:text-xl font-black text-white">لوحة الإدارة والتحكم — وتين</h2>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] font-mono font-bold">
                  ADMIN LEVEL 1
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                متابعة حركة المنصة الحية، إدارة حسابات المستخدمين، الإشعارات العامة، ومعايير الأمان
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-stretch sm:self-auto justify-between sm:justify-end">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#121a2a] border border-slate-800 text-[11px] text-slate-300">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span>الخادم متصل (SSE Active)</span>
            </div>

            <button
              onClick={() => fetchStats(true)}
              disabled={refreshing}
              className="px-3 py-1.5 rounded-xl bg-[#141d2e] hover:bg-[#1a253a] border border-slate-800 text-xs font-semibold text-slate-200 flex items-center gap-1.5 transition active:scale-95"
              title="تحديث البيانات فوراً"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-emerald-400 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">تحديث فوري</span>
            </button>

            <button
              onClick={handleExportBackup}
              className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-600/30 to-teal-600/30 hover:bg-emerald-600/40 border border-emerald-500/30 text-xs font-semibold text-emerald-300 flex items-center gap-1.5 transition active:scale-95"
              title="تنزيل نسخة احتياطية من قاعدة البيانات"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">نسخة احتياطية</span>
            </button>
          </div>
        </div>

        {/* Action Toast Feedback */}
        {actionFeedback && (
          <div className="p-3 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-bold flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span>{actionFeedback}</span>
          </div>
        )}

        {/* Error Feedback */}
        {errorMessage && (
          <div className="p-3 rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs font-bold flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-400" />
              <span>{errorMessage}</span>
            </div>
            <button
              onClick={() => fetchStats(true)}
              className="underline text-[11px] hover:text-white"
            >
              إعادة المحاولة
            </button>
          </div>
        )}

        {/* Internal Admin Navigation Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar border-b border-slate-800">
          {[
            { id: 'overview', label: '📊 الإحصائيات العامة', icon: LayoutDashboard },
            { id: 'users', label: `👥 إدارة المستخدمين (${stats?.totalUsers || 0})`, icon: Users },
            { id: 'broadcast', label: '📢 إرسال إشعار عام', icon: Bell },
            { id: 'conversations', label: `💬 المحادثات والقنوات (${stats?.totalConversations || 0})`, icon: MessageSquare },
            { id: 'reports', label: `🛡️ البلاغات (${stats?.reports?.length || 0})`, icon: AlertTriangle },
            { id: 'system', label: '⚙️ صحة الخادم والنظام', icon: Server },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  soundEffects.playTap();
                  setActiveTab(tab.id as AdminTab);
                }}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap flex items-center gap-2 transition ${
                  isActive
                    ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 shadow-sm'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-[#121826]'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* TAB 1: OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-5 animate-in fade-in duration-150">
            {/* Metric Cards Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="p-4 rounded-2xl bg-[#0e1422] border border-slate-800">
                <div className="text-[11px] font-semibold text-slate-400 mb-1 flex items-center justify-between">
                  <span>المستخدمون</span>
                  <Users className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="text-2xl font-black text-white font-mono">{stats?.totalUsers || 0}</div>
                <div className="text-[10px] text-emerald-400/80 mt-1 font-semibold">
                  {stats?.users?.filter((u: any) => u.isVerified).length || 0} موثقين ✓
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-[#0e1422] border border-slate-800">
                <div className="text-[11px] font-semibold text-slate-400 mb-1 flex items-center justify-between">
                  <span>المحادثات والقنوات</span>
                  <Radio className="w-4 h-4 text-teal-400" />
                </div>
                <div className="text-2xl font-black text-white font-mono">{stats?.totalConversations || 0}</div>
                <div className="text-[10px] text-teal-400/80 mt-1 font-semibold">
                  {stats?.conversations?.filter((c: any) => c.type !== 'direct').length || 0} جماعية وقنوات
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-[#0e1422] border border-slate-800">
                <div className="text-[11px] font-semibold text-slate-400 mb-1 flex items-center justify-between">
                  <span>إجمالي الرسائل</span>
                  <MessageSquare className="w-4 h-4 text-indigo-400" />
                </div>
                <div className="text-2xl font-black text-white font-mono">{stats?.totalMessages || 0}</div>
                <div className="text-[10px] text-indigo-400/80 mt-1 font-semibold">مشفرة بالكامل 🔒</div>
              </div>

              <div className="p-4 rounded-2xl bg-[#0e1422] border border-slate-800">
                <div className="text-[11px] font-semibold text-slate-400 mb-1 flex items-center justify-between">
                  <span>الاتصالات اللحظية</span>
                  <Activity className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="text-2xl font-black text-emerald-400 font-mono">
                  {stats?.activeConnections || 1}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">عبر SSE مباشر</div>
              </div>

              <div className="p-4 rounded-2xl bg-[#0e1422] border border-slate-800">
                <div className="text-[11px] font-semibold text-slate-400 mb-1 flex items-center justify-between">
                  <span>القصص واللحظات</span>
                  <Sparkles className="w-4 h-4 text-amber-400" />
                </div>
                <div className="text-2xl font-black text-white font-mono">
                  {(stats?.totalStories || 0) + (stats?.totalMoments || 0)}
                </div>
                <div className="text-[10px] text-amber-400/80 mt-1 font-semibold">
                  {stats?.totalStories || 0} قصة • {stats?.totalMoments || 0} لحظة
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-[#0e1422] border border-slate-800">
                <div className="text-[11px] font-semibold text-slate-400 mb-1 flex items-center justify-between">
                  <span>المكالمات المسجلة</span>
                  <Clock className="w-4 h-4 text-violet-400" />
                </div>
                <div className="text-2xl font-black text-white font-mono">{stats?.totalCalls || 0}</div>
                <div className="text-[10px] text-violet-400/80 mt-1 font-semibold">صوت وفيديو P2P</div>
              </div>
            </div>

            {/* System Quick Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 rounded-3xl bg-[#0e1422] border border-slate-800 space-y-3">
                <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2">
                  <Server className="w-4 h-4 text-emerald-400" />
                  <span>موارد ومعلومات الخادم</span>
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">استهلاك الذاكرة (Heap):</span>
                    <span className="font-mono text-emerald-400 font-bold">{stats?.systemInfo?.memoryUsageMb || 36} MB</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">مدة تشغيل الخادم:</span>
                    <span className="font-mono text-slate-200">
                      {Math.floor((stats?.systemInfo?.uptimeSeconds || 120) / 60)} دقيقة
                    </span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">بيئة التشغيل:</span>
                    <span className="font-mono text-slate-200">{stats?.systemInfo?.platform || 'linux'} (Node {stats?.systemInfo?.nodeVersion || 'v20'})</span>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-3xl bg-[#0e1422] border border-slate-800 space-y-3">
                <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-400" />
                  <span>اشتراكات وتين VIP</span>
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">المشتركون في Premium:</span>
                    <span className="font-mono text-amber-400 font-bold">
                      {stats?.users?.filter((u: any) => u.isPremium).length || 0} مستخدم
                    </span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">طرق الدفع المفعلة:</span>
                    <span className="text-slate-200">Apple Pay / Google Pay / البطاقات</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">المزايا النشطة:</span>
                    <span className="text-slate-200">شارة التاج 👑، فقاعات ملونة، مكالمات غير محدودة</span>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-3xl bg-[#0e1422] border border-slate-800 space-y-3 flex flex-col justify-between">
                <div>
                  <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2 mb-2">
                    <Bell className="w-4 h-4 text-indigo-400" />
                    <span>إجراءات سريعة للمشرف</span>
                  </h3>
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    إرسال إشعار عاجل لكافة المستخدمين المتصلين حالياً أو تصدير قاعدة البيانات.
                  </p>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={() => setActiveTab('broadcast')}
                    className="flex-1 py-2 px-3 rounded-xl bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 text-indigo-300 font-semibold text-xs text-center transition"
                  >
                    بث إشعار عام 📢
                  </button>
                  <button
                    onClick={handleExportBackup}
                    className="flex-1 py-2 px-3 rounded-xl bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 font-semibold text-xs text-center transition"
                  >
                    تصدير JSON 💾
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: USERS & PERMISSIONS */}
        {activeTab === 'users' && (
          <div className="space-y-4 animate-in fade-in duration-150">
            {/* Search & Filter Bar */}
            <div className="p-4 rounded-2xl bg-[#0e1422] border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="ابحث بالاسم، المعرف، أو البريد..."
                  className="w-full py-2 px-3 pr-9 rounded-xl bg-[#141c2c] border border-slate-800 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 transition"
                />
                <Search className="w-4 h-4 text-slate-500 absolute right-3 top-2.5 pointer-events-none" />
              </div>

              <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto no-scrollbar">
                {[
                  { id: 'all', label: 'الكل' },
                  { id: 'admins', label: 'المشرفون 🛡️' },
                  { id: 'verified', label: 'الموثقون ✓' },
                  { id: 'premium', label: 'VIP 👑' },
                  { id: 'banned', label: 'المحظورون 🚫' },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => {
                      soundEffects.playTap();
                      setUserFilter(f.id as any);
                    }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                      userFilter === f.id
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                        : 'bg-[#141c2c] text-slate-400 border border-slate-800 hover:text-white'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Users Table / List */}
            <div className="rounded-3xl bg-[#0e1422] border border-slate-800 overflow-hidden shadow-xl">
              <div className="overflow-x-auto">
                <table className="w-full text-right text-xs">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400 bg-[#0a0f1a]">
                      <th className="py-3 px-4 font-semibold">المستخدم</th>
                      <th className="py-3 px-4 font-semibold">المعرف</th>
                      <th className="py-3 px-4 font-semibold">البلد / البريد</th>
                      <th className="py-3 px-4 font-semibold">الحالة والرتب</th>
                      <th className="py-3 px-4 font-semibold text-left">إجراءات الإشراف والتحكم</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-slate-500">
                          لا يوجد مستخدمون يطابقون معايير البحث والفلترة.
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u: any) => {
                        const isLoading = actionLoadingUser === u.username;
                        const isSelf = u.username === currentUser.username;
                        const isOwner = u.username === 'aymen';

                        return (
                          <tr key={u.username || u.id} className="hover:bg-[#131b2c]/60 transition">
                            {/* User Avatar & Name */}
                            <td className="py-3.5 px-4">
                              <div className="flex items-center gap-2.5">
                                <div className="relative">
                                  <img
                                    src={u.avatar}
                                    alt={u.displayName}
                                    className={`w-9 h-9 rounded-xl object-cover ring-1 ${
                                      u.isPremium ? 'ring-amber-400' : 'ring-slate-700'
                                    }`}
                                  />
                                  {u.isPremium && (
                                    <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-amber-500 text-slate-950 rounded-full flex items-center justify-center text-[8px] font-black">
                                      👑
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <div className="font-bold text-white flex items-center gap-1.5">
                                    <span>{u.displayName}</span>
                                    {u.isVerified && (
                                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                    )}
                                  </div>
                                  <div className="text-[10px] text-slate-400 font-mono">
                                    انضم: {new Date(u.joinedDate || Date.now()).toLocaleDateString('ar-EG')}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Username */}
                            <td className="py-3.5 px-4 font-mono text-slate-300">
                              @{u.username}
                            </td>

                            {/* Country & Email */}
                            <td className="py-3.5 px-4 text-slate-400">
                              <div>{u.country || 'غير محدد'}</div>
                              <div className="text-[10px] font-mono text-slate-500">{u.email || '-'}</div>
                            </td>

                            {/* Badges & Status */}
                            <td className="py-3.5 px-4">
                              <div className="flex flex-wrap items-center gap-1">
                                {u.isAdmin && (
                                  <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px] font-bold flex items-center gap-1">
                                    <Shield className="w-3 h-3" />
                                    <span>مشرف</span>
                                  </span>
                                )}
                                {u.isPremium && (
                                  <span className="px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] font-bold flex items-center gap-1">
                                    <Crown className="w-3 h-3" />
                                    <span>VIP</span>
                                  </span>
                                )}
                                {u.isBanned ? (
                                  <span className="px-2 py-0.5 rounded-md bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[10px] font-bold">
                                    محظور 🚫
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded-md bg-teal-500/15 text-teal-300 border border-teal-500/30 text-[10px] font-bold">
                                    نشط 🟢
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Action Buttons */}
                            <td className="py-3.5 px-4 text-left">
                              <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                {/* Toggle Verify */}
                                <button
                                  onClick={() => handleUserAction(u.username, 'toggle_verify')}
                                  disabled={isLoading}
                                  className={`px-2.5 py-1 rounded-lg border font-semibold text-[11px] transition ${
                                    u.isVerified
                                      ? 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'
                                      : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25'
                                  }`}
                                  title="تعديل شارة التوثيق"
                                >
                                  {u.isVerified ? 'إلغاء التوثيق' : 'توثيق ✓'}
                                </button>

                                {/* Toggle Admin */}
                                {!isOwner && (
                                  <button
                                    onClick={() => handleUserAction(u.username, 'toggle_admin')}
                                    disabled={isLoading}
                                    className={`px-2.5 py-1 rounded-lg border font-semibold text-[11px] transition ${
                                      u.isAdmin
                                        ? 'bg-indigo-950 text-indigo-300 border-indigo-700'
                                        : 'bg-[#141b2a] text-slate-300 border-slate-700 hover:text-white'
                                    }`}
                                    title="ترقية إلى مشرف أو سحب الصلاحية"
                                  >
                                    {u.isAdmin ? 'سحب الإشراف' : 'ترقية لمشرف 🛡️'}
                                  </button>
                                )}

                                {/* Toggle VIP / Premium */}
                                <button
                                  onClick={() => handleUserAction(u.username, 'toggle_premium')}
                                  disabled={isLoading}
                                  className={`px-2.5 py-1 rounded-lg border font-semibold text-[11px] transition ${
                                    u.isPremium
                                      ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                                      : 'bg-[#141b2a] text-amber-400/80 border-slate-700 hover:text-amber-300'
                                  }`}
                                  title="منح أو سحب اشتراك وتين بريميوم"
                                >
                                  {u.isPremium ? 'سحب VIP' : 'منح VIP 👑'}
                                </button>

                                {/* Ban / Unban */}
                                {!isOwner && !isSelf && (
                                  <button
                                    onClick={() => handleUserAction(u.username, u.isBanned ? 'unban' : 'ban')}
                                    disabled={isLoading}
                                    className={`px-2.5 py-1 rounded-lg border font-semibold text-[11px] transition ${
                                      u.isBanned
                                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                        : 'bg-rose-500/10 text-rose-400 border-rose-500/30 hover:bg-rose-500/20'
                                    }`}
                                    title={u.isBanned ? 'إلغاء حظر الحساب' : 'حظر الحساب من المنصة'}
                                  >
                                    {u.isBanned ? 'فك الحظر' : 'حظر 🚫'}
                                  </button>
                                )}

                                {/* Clear Lockout */}
                                {u.failedLoginAttempts?.lockedUntil > Date.now() && (
                                  <button
                                    onClick={() => handleUserAction(u.username, 'clear_lockout')}
                                    disabled={isLoading}
                                    className="px-2 py-1 rounded-lg bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 text-[10px] font-bold"
                                    title="فك قفل محاولات الدخول الخاطئة"
                                  >
                                    فك القفل 🔓
                                  </button>
                                )}

                                {/* Reset Password */}
                                {!isOwner && (
                                  <button
                                    onClick={() => {
                                      if (confirm(`هل أنت متأكد من إعادة تعيين كلمة مرور @${u.username} إلى (wateen123)؟`)) {
                                        handleUserAction(u.username, 'reset_password');
                                      }
                                    }}
                                    disabled={isLoading}
                                    className="p-1 rounded-lg bg-[#141b2a] border border-slate-800 text-slate-400 hover:text-amber-300 transition"
                                    title="إعادة تعيين كلمة المرور إلى wateen123"
                                  >
                                    <KeyRound className="w-3.5 h-3.5" />
                                  </button>
                                )}

                                {/* Delete User */}
                                {!isOwner && !isSelf && (
                                  <button
                                    onClick={() => {
                                      if (confirm(`تحذير: هل أنت متأكد من رغبتك بحذف المستخدم @${u.username} نهائياً؟ هذا الإجراء لا يمكن التراجع عنه!`)) {
                                        handleUserAction(u.username, 'delete_user');
                                      }
                                    }}
                                    disabled={isLoading}
                                    className="p-1 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 hover:bg-rose-500/20 transition"
                                    title="حذف الحساب نهائياً"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: BROADCAST ANNOUNCEMENT */}
        {activeTab === 'broadcast' && (
          <div className="max-w-2xl mx-auto space-y-5 animate-in fade-in duration-150">
            <div className="p-6 rounded-3xl bg-[#0e1422] border border-slate-800 shadow-xl space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-800">
                <Bell className="w-5 h-5 text-emerald-400" />
                <div>
                  <h3 className="text-sm font-bold text-white">إرسال إشعار وبث عام لجميع المستخدمين</h3>
                  <p className="text-[11px] text-slate-400">
                    سيصل هذا الإشعار اللحظي إلى كافة المستخدمين المتصلين في الوقت الفعلي عبر تقنية SSE
                  </p>
                </div>
              </div>

              {broadcastSuccess && (
                <div className="p-3 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-bold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>تم إرسال البث العام بنجاح لجميع المستخدمين المتصلين! 📢</span>
                </div>
              )}

              <form onSubmit={handleSendBroadcast} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1.5">
                    عنوان الإشعار
                  </label>
                  <input
                    type="text"
                    value={broadcastTitle}
                    onChange={(e) => setBroadcastTitle(e.target.value)}
                    placeholder="مثال: تحديث أمني جديد، صيانة مجدولة، أو ميزة جديدة..."
                    className="w-full py-2.5 px-3.5 rounded-xl bg-[#141b2a] border border-slate-800 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1.5">
                    مستوى الأهمية والنوع
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'info', label: 'إعلامي عادي ℹ️', border: 'border-slate-800', active: 'bg-indigo-600/20 border-indigo-500 text-indigo-300' },
                      { id: 'warning', label: 'تنبيه هام ⚠️', border: 'border-slate-800', active: 'bg-amber-600/20 border-amber-500 text-amber-300' },
                      { id: 'urgent', label: 'عاجل وفوري 🚨', border: 'border-slate-800', active: 'bg-rose-600/20 border-rose-500 text-rose-300' },
                    ].map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setBroadcastSeverity(s.id as any)}
                        className={`py-2 px-3 rounded-xl border text-xs font-bold transition text-center ${
                          broadcastSeverity === s.id
                            ? s.active
                            : 'bg-[#141b2a] text-slate-400 border-slate-800 hover:text-white'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1.5">
                    نص الإشعار أو الرسالة *
                  </label>
                  <textarea
                    rows={4}
                    value={broadcastText}
                    onChange={(e) => setBroadcastText(e.target.value)}
                    placeholder="اكتب هنا تفاصيل الرسالة الإدارية التي ستظهر لجميع المستخدمين..."
                    className="w-full py-2.5 px-3.5 rounded-xl bg-[#141b2a] border border-slate-800 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 transition resize-none"
                    required
                  />
                </div>

                {/* Live Preview */}
                {broadcastText.trim() && (
                  <div className="p-3.5 rounded-2xl bg-[#0a0f18] border border-slate-800 space-y-1">
                    <div className="text-[10px] text-slate-500 font-bold">معاينة ظهور الإشعار للمستخدمين:</div>
                    <div className="font-bold text-xs text-emerald-300">
                      📢 {broadcastTitle.trim() || 'إشعار من إدارة وتين'}
                    </div>
                    <div className="text-xs text-slate-200 leading-relaxed whitespace-pre-wrap">
                      {broadcastText}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={sendingBroadcast || !broadcastText.trim()}
                  className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 transition active:scale-95 disabled:opacity-40"
                >
                  {sendingBroadcast ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  <span>إرسال البث لجميع المستخدمين الآن</span>
                </button>
              </form>
            </div>
          </div>
        )}

        {/* TAB 4: CONVERSATIONS & CHANNELS */}
        {activeTab === 'conversations' && (
          <div className="space-y-4 animate-in fade-in duration-150">
            <div className="rounded-3xl bg-[#0e1422] border border-slate-800 overflow-hidden shadow-xl">
              <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-emerald-400" />
                    <span>المحادثات، القنوات، والمجموعات النشطة</span>
                  </h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    إجمالي الغرف المنشأة: {stats?.conversations?.length || 0}
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-right text-xs">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400 bg-[#0a0f1a]">
                      <th className="py-3 px-4 font-semibold">المحادثة / القناة</th>
                      <th className="py-3 px-4 font-semibold">النوع</th>
                      <th className="py-3 px-4 font-semibold">المشاركون</th>
                      <th className="py-3 px-4 font-semibold">عدد الرسائل</th>
                      <th className="py-3 px-4 font-semibold">تاريخ الإنشاء</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {(!stats?.conversations || stats.conversations.length === 0) ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-slate-500">
                          لا توجد محادثات مسجلة بعد.
                        </td>
                      </tr>
                    ) : (
                      stats.conversations.map((c: any, idx: number) => (
                        <tr key={`${c.id}_${idx}`} className="hover:bg-[#131b2c]/60 transition">
                          <td className="py-3 px-4 font-bold text-white">
                            <div className="flex items-center gap-2">
                              <span>{c.title}</span>
                              {c.isPinned && (
                                <span className="text-[10px] text-amber-400 font-normal">📌 مثبتة</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 rounded-md bg-[#162032] border border-slate-700 text-[10px] font-mono text-slate-300">
                              {c.type === 'direct' ? 'محادثة خاصة' : c.type === 'channel' ? 'قناة عامة' : c.type === 'ai' ? 'ذكاء اصطناعي' : 'مجموعة'}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-mono text-slate-300">
                            {c.participantsCount} عضو
                          </td>
                          <td className="py-3 px-4 font-mono text-emerald-400 font-bold">
                            {c.messagesCount} رسالة
                          </td>
                          <td className="py-3 px-4 text-slate-400 text-[11px]">
                            {new Date(c.createdAt || Date.now()).toLocaleDateString('ar-EG')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: REPORTS & MODERATION */}
        {activeTab === 'reports' && (
          <div className="space-y-4 animate-in fade-in duration-150">
            <div className="rounded-3xl bg-[#0e1422] border border-slate-800 overflow-hidden shadow-xl">
              <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold text-white flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <span>سجل البلاغات والشكاوى</span>
                  </h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    متابعة بلاغات المستخدمين ضد المحتوى أو الحسابات المخالفة
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-right text-xs">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400 bg-[#0a0f1a]">
                      <th className="py-3 px-4 font-semibold">المبلّغ</th>
                      <th className="py-3 px-4 font-semibold">الحساب المبلّغ عنه</th>
                      <th className="py-3 px-4 font-semibold">السبب والسياق</th>
                      <th className="py-3 px-4 font-semibold">الحالة</th>
                      <th className="py-3 px-4 font-semibold text-left">الإجراء</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {(!stats?.reports || stats.reports.length === 0) ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-slate-500">
                          <CheckCircle2 className="w-8 h-8 text-emerald-500/40 mx-auto mb-2" />
                          <span>لا توجد بلاغات معلقة حالياً — بيئة وتين آمنة ونظيفة ✨</span>
                        </td>
                      </tr>
                    ) : (
                      stats.reports.map((r: any) => (
                        <tr key={r.id} className="hover:bg-[#131b2c]/60 transition">
                          <td className="py-3 px-4 font-mono text-slate-300">
                            @{r.reporterUsername}
                          </td>
                          <td className="py-3 px-4 font-mono text-rose-400 font-bold">
                            @{r.targetUsername}
                          </td>
                          <td className="py-3 px-4 text-slate-200">
                            <div className="font-semibold">{r.reason}</div>
                            {r.context && <div className="text-[10px] text-slate-400">{r.context}</div>}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              r.status === 'resolved'
                                ? 'bg-emerald-500/20 text-emerald-300'
                                : r.status === 'dismissed'
                                ? 'bg-slate-800 text-slate-400'
                                : 'bg-amber-500/20 text-amber-300'
                            }`}>
                              {r.status === 'resolved' ? 'تم الحل ✓' : r.status === 'dismissed' ? 'تم التجاهل' : 'معلق ⏳'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-left">
                            <div className="flex items-center justify-end gap-1.5">
                              {r.status !== 'resolved' && (
                                <button
                                  onClick={() => handleResolveReport(r.id, 'resolved')}
                                  className="px-2.5 py-1 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 text-[11px] font-bold transition"
                                >
                                  حل البلاغ
                                </button>
                              )}
                              {r.status !== 'dismissed' && (
                                <button
                                  onClick={() => handleResolveReport(r.id, 'dismissed')}
                                  className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 border border-slate-700 text-[11px] transition"
                                >
                                  تجاهل
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 6: SYSTEM & BACKUPS */}
        {activeTab === 'system' && (
          <div className="space-y-4 animate-in fade-in duration-150">
            <div className="p-6 rounded-3xl bg-[#0e1422] border border-slate-800 shadow-xl space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <Server className="w-5 h-5 text-emerald-400" />
                  <div>
                    <h3 className="text-sm font-bold text-white">النسخ الاحتياطي وإدارة بيانات الخادم</h3>
                    <p className="text-[11px] text-slate-400">
                      تصدير نسخة كاملة من سجلات المنصة والمستخدمين أو فحص حالة الذاكرة
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleExportBackup}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition active:scale-95"
                >
                  <Download className="w-4 h-4" />
                  <span>تصدير نسخة احتياطية الآن (JSON)</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                <div className="p-4 rounded-2xl bg-[#141b2a] border border-slate-800">
                  <div className="text-[11px] text-slate-400 mb-1">الذاكرة المستهلكة</div>
                  <div className="text-xl font-bold font-mono text-emerald-400">
                    {stats?.systemInfo?.memoryUsageMb || 38} MB
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">حالة الذاكرة ممتازة ومستقرة</div>
                </div>

                <div className="p-4 rounded-2xl bg-[#141b2a] border border-slate-800">
                  <div className="text-[11px] text-slate-400 mb-1">وقت التشغيل المستمر</div>
                  <div className="text-xl font-bold font-mono text-white">
                    {Math.floor((stats?.systemInfo?.uptimeSeconds || 120) / 60)} دقيقة
                  </div>
                  <div className="text-[10px] text-emerald-400 mt-1">0 انقطاعات مسجلة</div>
                </div>

                <div className="p-4 rounded-2xl bg-[#141b2a] border border-slate-800">
                  <div className="text-[11px] text-slate-400 mb-1">إجمالي سجلات النظام</div>
                  <div className="text-xl font-bold font-mono text-teal-400">
                    {(stats?.totalUsers || 0) + (stats?.totalMessages || 0) + (stats?.totalConversations || 0)} سجل
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">محفوظة في التخزين الدائم</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
